import { useMemo } from 'react';
import { buildScheduleCopyPayload, describeScheduleCopyPayload, type CopySnapshot } from './useScheduleCopy';

export interface ScheduleCopyPageInput {
  snapshot: CopySnapshot;
  typedName: string;
  currentScheduleIndex: number;
  generationInput: object & { openRevision?: number };
  openSectionIds: readonly string[] | null;

}

// Existing generation inputs and payloads are JSON data. Preserve array order,
// omitted versus explicit empty maps, and every actual value; object insertion
// order and allocation identity are not student intent.
const semanticJSON = (value: unknown): string => JSON.stringify(value, (_key, item) =>
  item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);

/** A representable snapshot is not permission to start a new create. */
export function useScheduleCopyPageState(input: ScheduleCopyPageInput) {
  const { actorId, source, fallbackTerm, isViewingSelectedSchedule, visibleOption, currentConfiguration,
    ownedDraftReady, generatedEligible } = input.snapshot;
  const candidateKey = useMemo(() => semanticJSON(describeScheduleCopyPayload({ actorId, source, fallbackTerm, isViewingSelectedSchedule, visibleOption, currentConfiguration })),
    [actorId, source, fallbackTerm, isViewingSelectedSchedule, visibleOption, currentConfiguration]);
  const intentPayload = useMemo(() => JSON.parse(candidateKey) as ReturnType<typeof describeScheduleCopyPayload>, [candidateKey]);
  // Counter changes may trigger a new snapshot, but only content is identity.
  const generationKey = useMemo(() => {
    const generationIntent = { ...input.generationInput };
    delete generationIntent.openRevision;
    return semanticJSON(generationIntent);
  }, [input.generationInput]);
  const optionKey = useMemo(() => semanticJSON(isViewingSelectedSchedule ? null : visibleOption), [isViewingSelectedSchedule, visibleOption]);
  const membershipKey = useMemo(() => semanticJSON(input.openSectionIds === null ? null : [...input.openSectionIds].sort()), [input.openSectionIds]);
  const intentKey = useMemo(() => semanticJSON({ payload: candidateKey, generation: generationKey, option: optionKey,
    view: isViewingSelectedSchedule, optionIndex: input.currentScheduleIndex,
    typedName: input.typedName, sourcePublic: source?.is_public, membership: membershipKey }),
    [candidateKey, generationKey, optionKey, isViewingSelectedSchedule, input.currentScheduleIndex, input.typedName, source?.is_public, membershipKey]);
  // Committed A -> B -> A inputs mint separate objects. Equivalent reference
  // refreshes retain the same token; the Copy hook keeps its strict fences.
  const draft = useMemo(() => ({ intentKey }), [intentKey]);
  const canDispatch = useMemo(() => buildScheduleCopyPayload({ actorId, source, fallbackTerm, isViewingSelectedSchedule, visibleOption, currentConfiguration, ownedDraftReady, generatedEligible }) !== null,
    [actorId, source, fallbackTerm, isViewingSelectedSchedule, visibleOption, currentConfiguration, ownedDraftReady, generatedEligible]);
  return { intentPayload, draft, canDispatch };
}
